<template>
  <div class="credit-transfer-management">
    <div class="header">
      <h2>积分转账管理</h2>
      <div class="actions">
        <el-button type="primary" @click="handleCreateTransfer">
          <el-icon><Plus /></el-icon>
          手动转账
        </el-button>
        <el-button @click="refreshData">
          <el-icon><Refresh /></el-icon>
          刷新
        </el-button>
      </div>
    </div>

    <!-- 搜索和筛选 -->
    <el-card class="filter-card">
      <el-form :inline="true" :model="searchForm">
        <el-form-item label="交易ID">
          <el-input v-model="searchForm.id" placeholder="请输入交易ID" clearable />
        </el-form-item>
        <el-form-item label="发送用户ID">
          <el-input v-model="searchForm.fromUserId" placeholder="请输入发送用户ID" clearable />
        </el-form-item>
        <el-form-item label="接收用户ID">
          <el-input v-model="searchForm.toUserId" placeholder="请输入接收用户ID" clearable />
        </el-form-item>
        <el-form-item label="积分类型">
          <el-select v-model="searchForm.creditTypeId" placeholder="请选择积分类型" clearable>
            <el-option label="帮扶分" value="1" />
            <el-option label="解债分" value="5" />
            <el-option label="现金分" value="6" />
          </el-select>
        </el-form-item>
        <el-form-item label="交易类型">
          <el-select v-model="searchForm.transactionType" placeholder="请选择交易类型" clearable>
            <el-option label="转账" value="transfer" />
            <el-option label="购买" value="purchase" />
            <el-option label="奖励" value="reward" />
            <el-option label="退款" value="refund" />
            <el-option label="兑换" value="exchange" />
            <el-option label="提现" value="withdraw" />
            <el-option label="充值" value="deposit" />
          </el-select>
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="searchForm.status" placeholder="请选择状态" clearable>
            <el-option label="待处理" value="pending" />
            <el-option label="已完成" value="completed" />
            <el-option label="失败" value="failed" />
            <el-option label="已取消" value="cancelled" />
          </el-select>
        </el-form-item>
        <el-form-item label="日期范围">
          <el-date-picker
            v-model="searchForm.dateRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            value-format="YYYY-MM-DD"
            clearable
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">
            <el-icon><Search /></el-icon>
            搜索
          </el-button>
          <el-button @click="resetSearch">
            <el-icon><Refresh /></el-icon>
            重置
          </el-button>
          <el-button type="success" @click="handleExport">
            <el-icon><Download /></el-icon>
            导出
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 统计卡片 -->
    <el-row :gutter="20" class="stat-cards">
      <el-col :span="6">
        <el-card class="stat-card">
          <div class="stat-content">
            <div class="stat-item">
              <h3>今日交易额</h3>
              <p class="stat-value">¥{{ formatAmount(stats.todayAmount) }}</p>
              <p class="stat-label">{{ stats.todayCount }}笔交易</p>
            </div>
            <el-icon class="stat-icon" color="#409eff"><Money /></el-icon>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card class="stat-card">
          <div class="stat-content">
            <div class="stat-item">
              <h3>本月交易额</h3>
              <p class="stat-value">¥{{ formatAmount(stats.monthAmount) }}</p>
              <p class="stat-label">{{ stats.monthCount }}笔交易</p>
            </div>
            <el-icon class="stat-icon" color="#67c23a"><TrendCharts /></el-icon>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card class="stat-card">
          <div class="stat-content">
            <div class="stat-item">
              <h3>待处理交易</h3>
              <p class="stat-value">{{ stats.pendingCount }}</p>
              <p class="stat-label">需要审核</p>
            </div>
            <el-icon class="stat-icon" color="#e6a23c"><Clock /></el-icon>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card class="stat-card">
          <div class="stat-content">
            <div class="stat-item">
              <h3>失败交易</h3>
              <p class="stat-value">{{ stats.failedCount }}</p>
              <p class="stat-label">需要处理</p>
            </div>
            <el-icon class="stat-icon" color="#f56c6c"><Warning /></el-icon>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 积分转账列表 -->
    <el-card>
      <el-table :data="transferList" v-loading="loading" stripe border>
        <el-table-column prop="id" label="交易ID" width="100" />
        <el-table-column label="发送方" width="180">
          <template #default="{ row }">
            <div v-if="row.fromUserId">
              <div class="user-info">
                <span class="user-id">ID: {{ row.fromUserId }}</span>
                <span class="user-name">{{ row.fromUserName || '系统' }}</span>
              </div>
              <div class="user-mobile">{{ row.fromUserMobile || '' }}</div>
            </div>
            <div v-else class="system-transfer">
              系统发放
            </div>
          </template>
        </el-table-column>
        <el-table-column label="接收方" width="180">
          <template #default="{ row }">
            <div v-if="row.toUserId">
              <div class="user-info">
                <span class="user-id">ID: {{ row.toUserId }}</span>
                <span class="user-name">{{ row.toUserName || '用户' }}</span>
              </div>
              <div class="user-mobile">{{ row.toUserMobile || '' }}</div>
            </div>
            <div v-else class="system-transfer">
              系统回收
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="creditTypeName" label="积分类型" width="100">
          <template #default="{ row }">
            <el-tag :type="getCreditTypeTag(row.creditTypeId)">
              {{ row.creditTypeName }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="amount" label="金额" width="120">
          <template #default="{ row }">
            <span class="amount" :class="{ 'positive': row.toUserId, 'negative': row.fromUserId }">
              {{ row.fromUserId ? '-' : '+' }}¥{{ formatAmount(row.amount) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="fee" label="手续费" width="100">
          <template #default="{ row }">
            <span v-if="row.fee > 0" class="fee">¥{{ formatAmount(row.fee) }}</span>
            <span v-else class="no-fee">-</span>
          </template>
        </el-table-column>
        <el-table-column prop="transactionType" label="交易类型" width="100">
          <template #default="{ row }">
            <el-tag size="small" :type="getTransactionTypeTag(row.transactionType)">
              {{ getTransactionTypeText(row.transactionType) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="100">
          <template #default="{ row }">
            <el-tag :type="getStatusTag(row.status)">
              {{ getStatusText(row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="description" label="描述" width="200" show-overflow-tooltip />
        <el-table-column prop="createdAt" label="创建时间" width="180">
          <template #default="{ row }">
            {{ formatDate(row.createdAt) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="180" fixed="right">
          <template #default="{ row }">
            <el-button type="primary" size="small" @click="handleView(row)">
              查看
            </el-button>
            <el-button 
              v-if="row.status === 'pending'" 
              type="success" 
              size="small" 
              @click="handleApprove(row)"
            >
              审核通过
            </el-button>
            <el-button 
              v-if="row.status === 'pending'" 
              type="danger" 
              size="small" 
              @click="handleReject(row)"
            >
              拒绝
            </el-button>
            <el-button 
              v-if="row.status === 'failed'" 
              type="warning" 
              size="small" 
              @click="handleRetry(row)"
            >
              重试
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination">
        <el-pagination
          v-model:current-page="pagination.current"
          v-model:page-size="pagination.size"
          :total="pagination.total"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>

    <!-- 创建转账对话框 -->
    <el-dialog
      v-model="dialogVisible"
      title="手动创建积分转账"
      width="500px"
      @close="resetForm"
    >
      <el-form ref="transferFormRef" :model="transferForm" :rules="transferRules" label-width="100px">
        <el-form-item label="发送用户ID" prop="fromUserId">
          <el-input 
            v-model="transferForm.fromUserId" 
            placeholder="留空表示系统发放"
            clearable
          />
        </el-form-item>
        <el-form-item label="接收用户ID" prop="toUserId">
          <el-input 
            v-model="transferForm.toUserId" 
            placeholder="留空表示系统回收"
            clearable
          />
        </el-form-item>
        <el-form-item label="积分类型" prop="creditTypeId">
          <el-select v-model="transferForm.creditTypeId" placeholder="请选择积分类型">
            <el-option label="帮扶分" value="1" />
            <el-option label="解债分" value="5" />
            <el-option label="现金分" value="6" />
          </el-select>
        </el-form-item>
        <el-form-item label="交易金额" prop="amount">
          <el-input 
            v-model="transferForm.amount" 
            placeholder="请输入交易金额"
            type="number"
            :min="0.01"
            step="0.01"
          >
            <template #append>元</template>
          </el-input>
        </el-form-item>
        <el-form-item label="手续费" prop="fee">
          <el-input 
            v-model="transferForm.fee" 
            placeholder="请输入手续费"
            type="number"
            :min="0"
            step="0.01"
          >
            <template #append>元</template>
          </el-input>
        </el-form-item>
        <el-form-item label="交易类型" prop="transactionType">
          <el-select v-model="transferForm.transactionType" placeholder="请选择交易类型">
            <el-option label="转账" value="transfer" />
            <el-option label="奖励" value="reward" />
            <el-option label="退款" value="refund" />
            <el-option label="兑换" value="exchange" />
            <el-option label="提现" value="withdraw" />
            <el-option label="充值" value="deposit" />
          </el-select>
        </el-form-item>
        <el-form-item label="描述" prop="description">
          <el-input 
            v-model="transferForm.description" 
            type="textarea" 
            :rows="3" 
            placeholder="请输入交易描述"
          />
        </el-form-item>
        <el-form-item label="参考ID" prop="referenceId">
          <el-input 
            v-model="transferForm.referenceId" 
            placeholder="如订单ID、任务ID等"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" @click="handleSubmit" :loading="submitting">
            创建
          </el-button>
        </span>
      </template>
    </el-dialog>

    <!-- 查看详情对话框 -->
    <el-dialog
      v-model="viewDialogVisible"
      title="交易详情"
      width="600px"
    >
      <el-descriptions :column="2" border v-if="currentTransfer">
        <el-descriptions-item label="交易ID">{{ currentTransfer.id }}</el-descriptions-item>
        <el-descriptions-item label="交易状态">
          <el-tag :type="getStatusTag(currentTransfer.status)">
            {{ getStatusText(currentTransfer.status) }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="发送方">
          <div v-if="currentTransfer.fromUserId">
            ID: {{ currentTransfer.fromUserId }} - {{ currentTransfer.fromUserName || '未知' }}
            <div class="sub-info">{{ currentTransfer.fromUserMobile }}</div>
          </div>
          <div v-else>系统发放</div>
        </el-descriptions-item>
        <el-descriptions-item label="接收方">
          <div v-if="currentTransfer.toUserId">
            ID: {{ currentTransfer.toUserId }} - {{ currentTransfer.toUserName || '未知' }}
            <div class="sub-info">{{ currentTransfer.toUserMobile }}</div>
          </div>
          <div v-else>系统回收</div>
        </el-descriptions-item>
        <el-descriptions-item label="积分类型">
          <el-tag :type="getCreditTypeTag(currentTransfer.creditTypeId)">
            {{ currentTransfer.creditTypeName }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="交易类型">
          <el-tag size="small" :type="getTransactionTypeTag(currentTransfer.transactionType)">
            {{ getTransactionTypeText(currentTransfer.transactionType) }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="交易金额">
          <span class="amount-highlight">¥{{ formatAmount(currentTransfer.amount) }}</span>
        </el-descriptions-item>
        <el-descriptions-item label="手续费">
          <span v-if="currentTransfer.fee > 0">¥{{ formatAmount(currentTransfer.fee) }}</span>
          <span v-else>无</span>
        </el-descriptions-item>
        <el-descriptions-item label="创建时间" :span="2">{{ formatDate(currentTransfer.createdAt) }}</el-descriptions-item>
        <el-descriptions-item label="交易描述" :span="2">{{ currentTransfer.description || '无' }}</el-descriptions-item>
        <el-descriptions-item label="参考ID" :span="2">{{ currentTransfer.referenceId || '无' }}</el-descriptions-item>
      </el-descriptions>
      <template #footer>
        <el-button @click="viewDialogVisible = false">关闭</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { 
  Plus, 
  Refresh, 
  Search,
  Download,
  Money,
  TrendCharts,
  Clock,
  Warning
} from '@element-plus/icons-vue'

// 状态管理
const loading = ref(false)
const dialogVisible = ref(false)
const viewDialogVisible = ref(false)
const submitting = ref(false)
const transferFormRef = ref()
const currentTransfer = ref(null)

// 搜索表单
const searchForm = reactive({
  id: '',
  fromUserId: '',
  toUserId: '',
  creditTypeId: '',
  transactionType: '',
  status: '',
  dateRange: []
})

// 转账表单
const transferForm = reactive({
  fromUserId: '',
  toUserId: '',
  creditTypeId: '1',
  amount: '',
  fee: '0',
  transactionType: 'transfer',
  description: '',
  referenceId: ''
})

// 表单验证规则
const transferRules = {
  creditTypeId: [
    { required: true, message: '请选择积分类型', trigger: 'change' }
  ],
  amount: [
    { required: true, message: '请输入交易金额', trigger: 'blur' },
    { pattern: /^\d+(\.\d{1,2})?$/, message: '请输入正确的金额格式', trigger: 'blur' },
    { validator: (rule, value, callback) => {
      if (parseFloat(value) <= 0) {
        callback(new Error('金额必须大于0'))
      } else {
        callback()
      }
    }, trigger: 'blur' }
  ],
  fee: [
    { pattern: /^\d+(\.\d{1,2})?$/, message: '请输入正确的手续费格式', trigger: 'blur' }
  ],
  transactionType: [
    { required: true, message: '请选择交易类型', trigger: 'change' }
  ]
}

// 分页配置
const pagination = reactive({
  current: 1,
  size: 10,
  total: 0
})

// 统计信息
const stats = reactive({
  todayAmount: 12500.50,
  todayCount: 42,
  monthAmount: 285000.75,
  monthCount: 856,
  pendingCount: 15,
  failedCount: 8
})

// 转账列表数据（模拟数据）
const transferList = ref([
  {
    id: 1001,
    fromUserId: 1001,
    fromUserName: '张三',
    fromUserMobile: '13800000001',
    toUserId: 1002,
    toUserName: '李四',
    toUserMobile: '13800000002',
    creditTypeId: 1,
    creditTypeName: '通用分',
    amount: 1000.00,
    fee: 0.00,
    transactionType: 'transfer',
    status: 'completed',
    description: '亲友帮还',
    referenceId: 'ORDER_20260305120001',
    createdAt: '2026-03-05T12:00:00'
  },
  {
    id: 1002,
    fromUserId: null,
    fromUserName: '系统',
    toUserId: 1003,
    toUserName: '王五',
    toUserMobile: '13800000003',
    creditTypeId: 2,
    creditTypeName: '消费分',
    amount: 500.00,
    fee: 0.00,
    transactionType: 'reward',
    status: 'completed',
    description: '任务完成奖励',
    referenceId: 'TASK_20260305110001',
    createdAt: '2026-03-05T11:30:00'
  },
  {
    id: 1003,
    fromUserId: 1004,
    fromUserName: '赵六',
    fromUserMobile: '13800000004',
    toUserId: null,
    toUserName: '系统',
    creditTypeId: 1,
    creditTypeName: '通用分',
    amount: 2000.00,
    fee: 10.00,
    transactionType: 'withdraw',
    status: 'pending',
    description: '积分提现申请',
    referenceId: 'WITHDRAW_20260305100001',
    createdAt: '2026-03-05T10:00:00'
  },
  {
    id: 1004,
    fromUserId: 1005,
    fromUserName: '钱七',
    fromUserMobile: '13800000005',
    toUserId: 1006,
    toUserName: '孙八',
    toUserMobile: '13800000006',
    creditTypeId: 3,
    creditTypeName: '任务分',
    amount: 800.00,
    fee: 0.00,
    transactionType: 'transfer',
    status: 'failed',
    description: '亲友帮还转账失败',
    referenceId: 'TRANSFER_20260305090001',
    createdAt: '2026-03-05T09:00:00'
  }
])

// 方法
const getTransactionTypeText = (type) => {
  const map = {
    transfer: '转账',
    purchase: '购买',
    reward: '奖励',
    refund: '退款',
    exchange: '兑换',
    withdraw: '提现',
    deposit: '充值'
  }
  return map[type] || type
}

const getTransactionTypeTag = (type) => {
  const map = {
    transfer: '',
    purchase: 'success',
    reward: 'warning',
    refund: 'info',
    exchange: 'warning',
    withdraw: 'danger',
    deposit: 'success'
  }
  return map[type] || ''
}

const getCreditTypeTag = (typeId) => {
  const map = {
    1: 'warning',  // 通用分
    2: 'success',  // 消费分
    3: 'info'      // 任务分
  }
  return map[typeId] || ''
}

const getStatusText = (status) => {
  const map = {
    pending: '待处理',
    completed: '已完成',
    failed: '失败',
    cancelled: '已取消'
  }
  return map[status] || status
}

const getStatusTag = (status) => {
  const map = {
    pending: 'warning',
    completed: 'success',
    failed: 'danger',
    cancelled: 'info'
  }
  return map[status] || ''
}

const formatDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleString('zh-CN')
}

const formatAmount = (amount) => {
  if (!amount) return '0.00'
  return parseFloat(amount).toFixed(2)
}

const refreshData = () => {
  loading.value = true
  // 模拟API请求
  setTimeout(() => {
    loading.value = false
    ElMessage.success('数据已刷新')
  }, 500)
}

const handleSearch = () => {
  pagination.current = 1
  refreshData()
}

const resetSearch = () => {
  Object.keys(searchForm).forEach(key => {
    searchForm[key] = ''
  })
  searchForm.dateRange = []
  handleSearch()
}

const handleExport = () => {
  ElMessage.info('导出功能开发中...')
}

const handleSizeChange = (size) => {
  pagination.size = size
  refreshData()
}

const handleCurrentChange = (page) => {
  pagination.current = page
  refreshData()
}

const handleCreateTransfer = () => {
  dialogVisible.value = true
}

const handleView = (row) => {
  currentTransfer.value = row
  viewDialogVisible.value = true
}

const handleApprove = async (row) => {
  try {
    await ElMessageBox.confirm('确定要审核通过该交易吗？', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    })
    
    // 模拟API请求
    row.status = 'completed'
    ElMessage.success('交易审核通过')
    refreshData()
  } catch {
    // 用户取消
  }
}

const handleReject = async (row) => {
  try {
    await ElMessageBox.confirm('确定要拒绝该交易吗？', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    })
    
    // 模拟API请求
    row.status = 'cancelled'
    ElMessage.success('交易已拒绝')
    refreshData()
  } catch {
    // 用户取消
  }
}

const handleRetry = async (row) => {
  try {
    await ElMessageBox.confirm('确定要重试该交易吗？', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    })
    
    // 模拟API请求
    row.status = 'pending'
    ElMessage.success('交易已重新提交')
    refreshData()
  } catch {
    // 用户取消
  }
}

const resetForm = () => {
  if (transferFormRef.value) {
    transferFormRef.value.resetFields()
  }
  Object.keys(transferForm).forEach(key => {
    if (key !== 'creditTypeId' && key !== 'transactionType') {
      transferForm[key] = ''
    }
  })
  transferForm.creditTypeId = '1'
  transferForm.transactionType = 'transfer'
  transferForm.fee = '0'
}

const handleSubmit = async () => {
  if (!transferFormRef.value) return
  
  try {
    await transferFormRef.value.validate()
    
    submitting.value = true
    
    // 模拟API请求
    setTimeout(() => {
      submitting.value = false
      dialogVisible.value = false
      
      // 创建新的交易记录
      const newTransfer = {
        id: transferList.value.length + 1001,
        fromUserId: transferForm.fromUserId ? parseInt(transferForm.fromUserId) : null,
        fromUserName: transferForm.fromUserId ? '发送用户' : '系统',
        toUserId: transferForm.toUserId ? parseInt(transferForm.toUserId) : null,
        toUserName: transferForm.toUserId ? '接收用户' : '系统',
        creditTypeId: parseInt(transferForm.creditTypeId),
        creditTypeName: transferForm.creditTypeId === '1' ? '通用分' : 
                       transferForm.creditTypeId === '2' ? '消费分' : '任务分',
        amount: parseFloat(transferForm.amount),
        fee: parseFloat(transferForm.fee || 0),
        transactionType: transferForm.transactionType,
        status: 'completed',
        description: transferForm.description,
        referenceId: transferForm.referenceId,
        createdAt: new Date().toISOString()
      }
      
      transferList.value.unshift(newTransfer)
      ElMessage.success('积分转账创建成功')
      
      resetForm()
      refreshData()
    }, 500)
  } catch (error) {
    console.log('表单验证失败:', error)
  }
}

// 生命周期
onMounted(() => {
  refreshData()
})
</script>

<style scoped>
.credit-transfer-management {
  padding: 20px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.header h2 {
  margin: 0;
  font-size: 24px;
  color: #ffffff !important;
}

.actions {
  display: flex;
  gap: 10px;
}

.filter-card {
  margin-bottom: 20px;
}

.stat-cards {
  margin-bottom: 20px;
}

.stat-card {
  height: 100%;
}

.stat-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.stat-item {
  text-align: left;
}

.stat-value {
  font-size: 24px;
  font-weight: bold;
  color: #ffffff !important;
  margin: 5px 0;
}

.stat-label {
  font-size: 14px;
  color: #909399;
}

.stat-icon {
  font-size: 40px;
  opacity: 0.7;
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.user-info {
  display: flex;
  flex-direction: column;
}

.user-id {
  font-size: 12px;
  color: #909399;
}

.user-name {
  font-size: 14px;
  font-weight: 500;
  margin-top: 2px;
}

.user-mobile {
  font-size: 12px;
  color: #666;
  margin-top: 2px;
}

.system-transfer {
  color: #909399;
  font-style: italic;
}

.amount {
  font-weight: bold;
}

.amount.positive {
  color: #67c23a;
}

.amount.negative {
  color: #f56c6c;
}

.fee {
  color: #e6a23c;
  font-size: 12px;
}

.no-fee {
  color: #909399;
  font-size: 12px;
}

.sub-info {
  font-size: 12px;
  color: #909399;
}

.amount-highlight {
  font-size: 18px;
  font-weight: bold;
  color: #409eff;
}

:deep(.el-date-editor) {
  width: 240px;
}
</style>
